/**
 * A hand held on a panel control: a slider's thumb, a field's scrub label, the colour
 * square, a gradient stop being dragged.
 *
 * Such a control says what it is worth on every movement, and each of those reaches the
 * document as an edit. The canvas closes an undo step after every panel edit — right for a
 * typed value or a press, wrong for a drag, which came back as dozens of steps so one Undo
 * only moved it back a notch (QA 2026-09-24, inspector-numbers-F10). A control calls
 * `beginHold()` when the hand goes down; the hold ends by itself when the hand comes up
 * anywhere, and the canvas, listening, closes ONE step for the whole drag.
 */

type Listener = (held: boolean) => void;

const listeners = new Set<Listener>();
let held = false;

export const endHold = () => {
  window.removeEventListener('pointerup', endHold);
  window.removeEventListener('pointercancel', endHold);
  if (!held) return;
  held = false;
  for (const listener of listeners) listener(false);
};

/** A hand has gone down on a control that changes as it moves. */
export function beginHold(): void {
  if (held) return;
  held = true;
  for (const listener of listeners) listener(true);
  // Wherever the hand comes up — off the control, off the panel — the hold is over. Heard
  // as it bubbles out, so what the control does on its own let-go (a gradient stop pulled
  // off its bar) still belongs to the drag.
  window.addEventListener('pointerup', endHold);
  window.addEventListener('pointercancel', endHold);
}

/** Whether a control is being held right now. */
export function holding(): boolean {
  return held;
}

/** Told when a hold begins (true) and ends (false). Returns the way to stop listening. */
export function onHold(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
